import React, { useState, useMemo } from 'react';
import { get, partial, pick } from 'lodash';
import { SplitFactory, SplitTreatments } from '@splitsoftware/splitio-react';

import {
  ApiKeys,
  CreditCardBrandCode,
  PaymentDetailsCard,
  PaymentProcessor,
  SupportedPaymentMethod,
  PaymentSourceType,
  PaymentSourceInput,
  CreditCardPaymentSource,
} from '@zapier/payment/src/types';
import { Button, Text } from '@zapier/design-system';
import CreditCardForm from '@zapier/payment/src/components/CreditCardForm';
import CreditCardFormWithSetupIntents from '@zapier/payment/src/components/CreditCardForm/CreditCardFormWithSetupIntents';
import PaymentInfo from '@zapier/payment/src/components/PaymentInfo';
import PaymentMethodImage from '@zapier/payment/src/components/PaymentMethodImage';
import { useSize } from '@zapier/payment/src/hooks/useSize';

import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';
import { Currency } from '@zapier/currency';

type Props = {
  accountId: number | undefined;
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Currently select method */
  currentMethod: PaymentSourceType | null;
  /** Error message to show */
  error?: string | undefined | null;
  /** Do not allow user to cancel card edit */
  forceCardUpdate: boolean;
  /** Hide switch component button */
  hideSwitch: boolean;
  /** Show editor */
  isEditing: boolean;
  /** Show loading */
  isEditingInFlight: boolean;
  /** Call to cancel edit */
  onCancelEdit: () => void;
  /** Call to start edit */
  onEdit: () => void;
  /** Call when editor throws an error */
  onErrorEdit: (error: Error) => void;
  /** Call to save current edit */
  onSaveEdit: (b: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType) => void;
  /** User's currently saved card details */
  savedSource: CreditCardPaymentSource | null;
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>;
  /** The array of payment methods selectable from the "Switch Payment Method" dropdown */
  switchMethodOptions: SupportedPaymentMethod[];
  /** The selected currency */
  selectedCurrency: Currency;
  /** This is very temporary until we remove the split.io flag. This is meant to temporarily turn on localhost mode for jest tests. */
  splitLocalhostMode?: boolean;
};

type UiRenderer<Props, ExtraData extends Record<string, unknown>> = (
  props: Props,
  extraData?: ExtraData
) => JSX.Element | null;

type RenderLabelProps = Pick<Props, 'savedSource' | 'isEditing' | 'hideSwitch'>;
type RenderLabelExtraData = {
  isSmall: boolean;
  cardBrand: CreditCardBrandCode | null | undefined;
};

const renderLabel: UiRenderer<RenderLabelProps, RenderLabelExtraData> = (
  props,
  extraData?
) => {
  return props.savedSource && !props.isEditing ? (
    <PaymentInfo paymentSource={props.savedSource} />
  ) : (
    <React.Fragment>
      {extraData?.isSmall && !props.hideSwitch ? null : (
        <PaymentMethodImage imageType={extraData?.cardBrand} isSmall={true} />
      )}
      <Text tag="h3" type="paragraph3Bold">
        {props.savedSource ? 'Edit Card' : 'Add Card'}
      </Text>
    </React.Fragment>
  );
};

type RenderAsideProps = Pick<
  Props,
  | 'isEditing'
  | 'onEdit'
  | 'savedSource'
  | 'hideSwitch'
  | 'currentMethod'
  | 'onSelectSource'
  | 'switchMethodOptions'
>;
type RenderAsideExtraData = { isSmall: boolean };

const renderAside: UiRenderer<RenderAsideProps, RenderAsideExtraData> = (
  props,
  extraData
) => {
  if (!props.isEditing) {
    return (
      <Button color="secondary" onClick={props.onEdit}>
        {props.savedSource
          ? `Edit${!extraData?.isSmall ? ' Payment Method' : ''}`
          : 'Add'}
      </Button>
    );
  }

  return props.hideSwitch ? null : (
    <PaymentMethodSelect
      currentMethod={props.currentMethod}
      onChange={props.onSelectSource}
      supportedMethods={props.switchMethodOptions}
    />
  );
};

const PaymentMethodCard: React.FC<Props> = props => {
  const [cardBrand, setCardBrand] = useState<
    CreditCardBrandCode | null | undefined
  >(null);
  const size = useSize();
  const isSmall = size === 'small';

  //@ts-ignore savedSource is not a type
  const cardDetails: PaymentDetailsCard = get(props, [
    'savedSource',
    'details',
  ]);

  const cardMethod = props.supportedMethods.find(
    m => m.method === PaymentSourceType.card
  );

  const labelProps = pick(props, ['savedSource', 'isEditing', 'hideSwitch']);
  const asideProps = pick(props, [
    'isEditing',
    'onEdit',
    'savedSource',
    'hideSwitch',
    'currentMethod',
    'onSelectSource',
    'switchMethodOptions',
  ]);

  // Using useMemo here, ensures that the deps are properly populated by our exhaustive-checks hook
  const renderLabelCallback = useMemo(
    () => partial(renderLabel, labelProps, { isSmall, cardBrand }),
    [labelProps, isSmall, cardBrand]
  );

  // Using useMemo here, ensures that the deps are properly populated by our exhaustive-checks hook
  const renderAsideCallback = useMemo(
    () => partial(renderAside, asideProps, { isSmall }),
    [asideProps, isSmall]
  );

  // These are temporary and will be removed after full rollout out SetupIntents/PaymentIntents
  const isProduction =
    typeof window !== 'undefined' &&
    window.location.hostname.includes('zapier.com');

  const authorizationKey = isProduction
    ? 'mctu4q1tvfa8hgn8kgt5doe0ev6h68th135'
    : 'tra7b4qvk62o373ivf9gc24547fp5cqdv6u0';

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={renderAsideCallback}
      renderLabel={renderLabelCallback}
    >
      <SplitFactory
        config={{
          core: {
            authorizationKey: props.splitLocalhostMode
              ? 'localhost'
              : authorizationKey,
            key: String(props.accountId),
          },
          ...((props.splitLocalhostMode ?? false) && {
            features: { billing_fe_payment_setup_intents: 'on' },
          }),
        }}
      >
        <SplitTreatments names={['billing_fe_payment_setup_intents']}>
          {({ treatments, isReady }) => {
            return isReady ? (
              treatments.billing_fe_payment_setup_intents.treatment === 'on' ? (
                <CreditCardFormWithSetupIntents
                  apiKeys={props.apiKeys}
                  error={props.error}
                  hasCancelButton={!props.forceCardUpdate}
                  hasPaymentSource={Boolean(props.savedSource)}
                  isInFlight={props.isEditingInFlight}
                  onCancel={props.onCancelEdit}
                  onCardNumberChange={setCardBrand}
                  onError={props.onErrorEdit}
                  onSave={props.onSaveEdit}
                  paymentProcessor={get(
                    cardMethod,
                    'paymentProcessor',
                    PaymentProcessor.stripe
                  )}
                  savedCard={cardDetails}
                  selectedCurrency={props.selectedCurrency}
                  supportedSubmethods={props.supportedMethods.map(
                    sm => sm.submethod
                  )}
                />
              ) : (
                <CreditCardForm
                  apiKeys={props.apiKeys}
                  error={props.error}
                  hasCancelButton={!props.forceCardUpdate}
                  hasPaymentSource={Boolean(props.savedSource)}
                  isInFlight={props.isEditingInFlight}
                  onCancel={props.onCancelEdit}
                  onCardNumberChange={setCardBrand}
                  onError={props.onErrorEdit}
                  onSave={props.onSaveEdit}
                  paymentProcessor={get(
                    cardMethod,
                    'paymentProcessor',
                    PaymentProcessor.stripe
                  )}
                  savedCard={cardDetails}
                  selectedCurrency={props.selectedCurrency}
                  supportedSubmethods={props.supportedMethods.map(
                    sm => sm.submethod
                  )}
                />
              )
            ) : null;
          }}
        </SplitTreatments>
      </SplitFactory>
    </PaymentMethod>
  );
};

export default PaymentMethodCard;
